import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CategoryAccordion from './CategoryAccordion';
import { useBudget } from '../../context/BudgetContext';
import { formatCAD } from '../../utils/formatCurrency';
import {
  updateFixedExpense, removeFixedExpense,
  updateVariableExpense, removeVariableExpense,
} from '../../context/budgetActions';

export default function BudgetBreakdownEditor() {
  const { t } = useTranslation();
  const { state, dispatch, derived } = useBudget();

  const budgetMax = derived.monthlyIncome > 0 ? derived.monthlyIncome * 0.35 : 1500;

  const fixedTotal = derived.monthlyFixedExpenses || 0;
  const variableTotal = derived.monthlyVariableExpenses || 0;
  const grandTotal = derived.monthlyTotalExpenses || 0;
  const fixedPct = grandTotal > 0 ? Math.round((fixedTotal / grandTotal) * 100) : 0;
  const variablePct = grandTotal > 0 ? Math.round((variableTotal / grandTotal) * 100) : 0;

  // Build top spending categories sorted by total
  const topCategories = useMemo(() => {
    const all = {};
    Object.entries(derived.fixedByCategory).forEach(([cat, data]) => {
      all[cat] = (all[cat] || 0) + data.total;
    });
    Object.entries(derived.variableByCategory).forEach(([cat, data]) => {
      all[cat] = (all[cat] || 0) + data.total;
    });
    return Object.entries(all)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [derived.fixedByCategory, derived.variableByCategory]);

  const maxCategoryAmount = topCategories.length > 0 ? topCategories[0][1] : 1;

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5, color: '#1E3A5F' }}>
          {t('budgetPlanner.breakdown.title', 'Desglose del presupuesto')}
        </Typography>

        {/* Summary strip: Fixed vs Variable */}
        {grandTotal > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, md: 3 },
              flexWrap: 'wrap',
              mb: 2,
              px: 2,
              py: 1.25,
              bgcolor: '#F7F9FC',
              borderRadius: 2,
              border: '1px solid #E6E9EE',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1E3A5F', flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: '#1E3A5F' }}>
                {t('budgetPlanner.breakdown.fixed', 'Gastos fijos')}:
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#1E3A5F' }}>
                {formatCAD(fixedTotal)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                ({fixedPct}%)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#C0C7D0' }}>|</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F9A825', flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: '#7D5A00' }}>
                {t('budgetPlanner.breakdown.variable', 'Gastos variables')}:
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#7D5A00' }}>
                {formatCAD(variableTotal)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                ({variablePct}%)
              </Typography>
            </Box>
          </Box>
        )}

        {/* Category mini-bars */}
        {topCategories.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              {t('budgetPlanner.breakdown.topCategories', 'Principales categorias')}
            </Typography>
            {topCategories.map(([cat, amount]) => {
              const pct = grandTotal > 0 ? Math.round((amount / grandTotal) * 100) : 0;
              const barWidth = maxCategoryAmount > 0 ? (amount / maxCategoryAmount) * 100 : 0;
              return (
                <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                  <Typography variant="caption" sx={{ width: 110, flexShrink: 0, color: '#1E3A5F', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cat}
                  </Typography>
                  <Box sx={{ flex: 1, height: 14, bgcolor: '#E6E9EE', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ width: `${barWidth}%`, height: '100%', bgcolor: '#1E3A5F', borderRadius: 1, transition: 'width 0.4s ease' }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 70, textAlign: 'right', flexShrink: 0, color: '#1E3A5F', fontWeight: 600 }}>
                    {formatCAD(amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ width: 32, textAlign: 'right', flexShrink: 0, color: '#5F6B7A' }}>
                    {pct}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {(topCategories.length > 0 || grandTotal > 0) && <Divider sx={{ mb: 2 }} />}

        {/* Fixed expenses */}
        {Object.keys(derived.fixedByCategory).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('budgetPlanner.breakdown.fixed', 'Gastos fijos')}
            </Typography>
            {Object.entries(derived.fixedByCategory).map(([cat, data]) => (
              <CategoryAccordion
                key={`fixed-${cat}`}
                category={cat}
                items={data.items}
                categoryTotal={data.total}
                budgetMax={budgetMax}
                onUpdate={(item) => dispatch(updateFixedExpense(item))}
                onDelete={(id) => dispatch(removeFixedExpense(id))}
              />
            ))}
          </Box>
        )}

        {Object.keys(derived.fixedByCategory).length > 0 && Object.keys(derived.variableByCategory).length > 0 && (
          <Divider sx={{ my: 2 }} />
        )}

        {/* Variable expenses */}
        {Object.keys(derived.variableByCategory).length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('budgetPlanner.breakdown.variable', 'Gastos variables')}
            </Typography>
            {Object.entries(derived.variableByCategory).map(([cat, data]) => (
              <CategoryAccordion
                key={`var-${cat}`}
                category={cat}
                items={data.items}
                categoryTotal={data.total}
                budgetMax={budgetMax}
                onUpdate={(item) => dispatch(updateVariableExpense(item))}
                onDelete={(id) => dispatch(removeVariableExpense(id))}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
